import cx from 'classnames';
import { memo } from 'react';
import { ClanLeagueSignInline, Tooltip, TooltipBody } from '@wg/wows-react-uikit';

import { toRoman } from '~/helpers/formatting';
import { getLeagueNameByNumber, isLastSeasonRegular } from '~/helpers/ladder';
import { t } from '~/helpers/localization';
import { TEAMS_NAMES } from '~/helpers/teams';

import styles from './ClanLeagueSign.scss';

import type { WowsLadderRatingTeam } from '~/Actions/ActionClanProfile';
import type { ILadderSeason } from '~/store/slices/settingsSlice';

type ClanLeagueSignTooltipProps = {
  clanDivision?: number;
  clanLeague?: number;
  isFullSize: boolean;
  leadingTeamNumber?: number;
  fewTeams?: boolean;
  teamsRating?: WowsLadderRatingTeam[] | null;
  lastSeason: ILadderSeason | null;
  lastSeasonId: number;
};

const TEAMS_TOOLTIP_TEXT = {
  leadingTeam: t('Ведущий рейтинг клана'),
  ratingInCurrentSeason: t('Рейтинг клана в текущем сезоне клановых боев.'),
  ratingInLastRegularSeason: t('Рейтинг клана в последнем сезоне клановых боев.'),
};

const ClanLeagueSignTooltip = ({
  clanDivision,
  clanLeague,
  isFullSize,
  leadingTeamNumber,
  fewTeams,
  teamsRating,
  lastSeason,
  lastSeasonId,
}: ClanLeagueSignTooltipProps) => {
  if (!clanLeague) {
    return null;
  }

  const leagueName = getLeagueNameByNumber(clanLeague, lastSeasonId);
  const division = clanDivision;
  const l10nLeague = t('Лига:');
  const l10nDivision = t('Дивизион:');
  const leagueDivisionText = `${l10nLeague} ${leagueName}, ${l10nDivision} ${toRoman(division ?? 0)}`;
  const seasonText = isLastSeasonRegular()
    ? TEAMS_TOOLTIP_TEXT.ratingInCurrentSeason
    : TEAMS_TOOLTIP_TEXT.ratingInLastRegularSeason;

  if (isFullSize) {
    return (
      <Tooltip>
        <TooltipBody>
          <div className={styles.tooltipHeader}>{leagueDivisionText}</div>
          <div>{seasonText}</div>
        </TooltipBody>
      </Tooltip>
    );
  }

  if (!teamsRating || !teamsRating.length) {
    return (
      <Tooltip>
        <TooltipBody>
          <div className={styles.tooltipTeam}>
            <div className={styles.tooltipHeader}>{leagueDivisionText}</div>
            <div>{seasonText}</div>
          </div>
        </TooltipBody>
      </Tooltip>
    );
  }

  const teams = [...teamsRating].sort((teamA, teamB) => {
    if (teamA.public_rating > teamB.public_rating) {
      return -1;
    }
    if (teamA.public_rating < teamB.public_rating) {
      return 1;
    }
    return 0;
  });

  const tooltipContent = teams.map((team) => {
    const isLeadingTeam = leadingTeamNumber === team.team_number;

    const teamText =
      fewTeams || teams.length > 1 ? (isLeadingTeam ? TEAMS_TOOLTIP_TEXT.leadingTeam : null) : seasonText;

    const currentTeamLeague = lastSeason?.leagues?.[team.league];
    const teamRatingText = `${l10nLeague} ${getLeagueNameByNumber(
      team.league,
      lastSeasonId,
    )}, ${l10nDivision} ${toRoman(team.division)}`;

    return (
      <div
        key={team.id}
        className={cx({
          [styles.leadingTeam]: isLeadingTeam && (fewTeams || teams.length > 1),
        })}
      >
        <TooltipBody>
          <div className={styles.TooltipLeagueSign}>
            <ClanLeagueSignInline color={currentTeamLeague?.color || ''} title={toRoman(team.division)} />
          </div>
          <div className={styles.tooltipTeam}>
            <div className={styles.tooltipHeader}>{TEAMS_NAMES[team.team_number]}</div>
            <div className={styles.tooltipLeagueDivisionText}>{teamRatingText}</div>
            <div>{teamText}</div>
          </div>
        </TooltipBody>
      </div>
    );
  });

  return <Tooltip>{tooltipContent}</Tooltip>;
};

export default memo(ClanLeagueSignTooltip);
